import React, { useState, useEffect } from 'react';
import { useVrp } from '../../context/VrpContext';
import LocationList from '../LocationList';
import '../styles/modes/customMode.css';
import { 
  isCustomMode
} from '../../Services';

// Import sub-components
import ModeHelpTooltip from './ModeHelpTooltip';
import VehiclePathGrid from './VehiclePathGrid';
import RemainingPoints from './RemainingPoints';
import PathActions from './PathActions';

// Import custom hooks
import { useVehiclePath } from './hooks';

/**
 * Custom Mode Component for Vehicle Routing Problem
 * Uses useVehiclePath hook for path management
 */
export default function CustomMode({ 
  locations, 
  startId, 
  setLocations, 
  setStartId, 
  setPath,
  path = [], 
  setMapPointSelect,
  currentModeRef,
  vehicleCount = 2,
  onVehicleCountChange,
  maxCapacity = 0,           
  onMaxCapacityChange        
}) {
  // Access VrpContext for unfinished path state
  const { 
    setVehiclePaths: setContextVehiclePaths, 
    setSelectedVehicleIndex: setContextSelectedVehicleIndex,
    setPathComplete: setContextPathComplete,
    pathComplete: contextPathComplete
  } = useVrp();
  
  // Help tooltip state
  const [showHelp, setShowHelp] = useState(false);
  
  // Internal vehicle count management if no external handler provided
  const [internalVehicleCount, setInternalVehicleCount] = useState(vehicleCount);
  
  // Internal max capacity management if no external handler provided
  const [internalMaxCapacity, setInternalMaxCapacity] = useState(maxCapacity);
  
  // Actual vehicle count currently used - single definition for the component
  const actualVehicleCount = onVehicleCountChange ? vehicleCount : internalVehicleCount;
  
  // Toggle help visibility
  const toggleHelp = () => setShowHelp(!showHelp);
  
  // Synchronize internal max capacity with external prop
  useEffect(() => {
    setInternalMaxCapacity(maxCapacity);
  }, [maxCapacity]);
  
  // Use the vehicle path hook to manage all path related state and logic
  const {
    vehiclePaths,
    selectedVehicleIndex,
    pathComplete,
    selectVehiclePath,
    resetVehiclePath,
    resetAllPaths,
    removePointFromPath,
    addPointToSelectedVehicle,
    confirmAllPaths,
    getRemainingPoints,
    undoLastPoint
  } = useVehiclePath({
    locations,
    startId,
    vehicleCount: actualVehicleCount,
    setPath,
    path,
    currentModeRef,
    isCustomMode,
    maxCapacity: onMaxCapacityChange ? maxCapacity : internalMaxCapacity,
    setMapPointSelect,
    pathComplete: contextPathComplete,
    setPathComplete: setContextPathComplete
  });
  
  // Sync vehicle paths and selected vehicle index with context for unfinished path visualization
  useEffect(() => {
    setContextVehiclePaths(vehiclePaths);
  }, [vehiclePaths, setContextVehiclePaths]);
  
  // Sync selected vehicle index with context
  useEffect(() => {
    setContextSelectedVehicleIndex(selectedVehicleIndex);
  }, [selectedVehicleIndex, setContextSelectedVehicleIndex]);
  
  // Handle vehicle count change
  const handleVehicleCountChange = (count) => {
    if (onVehicleCountChange) {
      onVehicleCountChange(count);
    } else {
      setInternalVehicleCount(count);
    }
  };
  
  // Handle max capacity change
  const handleMaxCapacityChange = (capacity) => {
    if (onMaxCapacityChange) {
      onMaxCapacityChange(capacity);
    } else {
      setInternalMaxCapacity(capacity);
    }
  };
  
  return (
    <div className="custom-mode-container">
      <div className="mode-description">
        <div className="mode-title-container">
          <h3>Custom Path</h3>
          <button 
            className="mode-help-button"
            onClick={toggleHelp}
            title="Help for Custom Path mode"
          >
            ?
          </button>
        </div>
        <p>Set each vehicle's path manually, select a vehicle first, then add path points. Each point (excluding start point) can only be added to one path.</p>
      </div>
      
      {/* Help tooltip */}
      {showHelp && <ModeHelpTooltip onClose={() => setShowHelp(false)} />}
      
      {/* Use updated LocationList, support vehicle count setting */}
      <LocationList 
        locations={locations}
        startId={startId}
        mode="Custom Path"
        readOnly={false}
        showActions={true}
        allowDelete={false}
        allowSetStart={true}
        onLocationChange={setLocations}
        onStartIdChange={setStartId}
        vehicleCount={actualVehicleCount}
        onVehicleCountChange={handleVehicleCountChange}
        showVehicleCount={true}
        maxCapacity={onMaxCapacityChange ? maxCapacity : internalMaxCapacity}
        onMaxCapacityChange={handleMaxCapacityChange}
        showMaxCapacity={true}
      />
      
      {locations.length > 0 ? (
        <div className="custom-path-builder">
          {startId ? (
            <>
              {/* Vehicle path selection grid */}
              <VehiclePathGrid 
                vehiclePaths={vehiclePaths}
                selectedVehicleIndex={selectedVehicleIndex}
                maxCapacity={onMaxCapacityChange ? maxCapacity : internalMaxCapacity}
                startId={startId}
                pathComplete={contextPathComplete}
                selectVehiclePath={selectVehiclePath}
                resetVehiclePath={resetVehiclePath}
                removePointFromPath={removePointFromPath}
                vehicleCount={actualVehicleCount}
                handleVehicleCountChange={handleVehicleCountChange}
                locations={locations}
                undoLastPoint={undoLastPoint}
              />
              
              {/* Available points component */}
              <RemainingPoints 
                selectedVehicleIndex={selectedVehicleIndex}
                remainingPoints={getRemainingPoints()}
                addPointToSelectedVehicle={addPointToSelectedVehicle}
              />
              
              {/* Path actions buttons */}
              <PathActions 
                resetAllPaths={resetAllPaths}
                confirmAllPaths={confirmAllPaths}
                vehiclePaths={vehiclePaths}
                locations={locations}
              />
            </>
          ) : (
            <div className="empty-path-message">
              <p>Please set a start point first, then start building your custom path.</p>
            </div>
          )}
        </div>
      ) : (
        <div className="no-locations-message">
          <p>Please add location points first before building paths.</p>
        </div>
      )}
    </div>
  );
} 